import type { ProcessingStreamEvent, Video } from '../services/api';

export type VideoPreviewFields = Pick<
  Video,
  | 'thumbnail_gcs_uri'
  | 'thumbnail_uri'
  | 'thumbnail_available'
  | 'gif_gcs_uri'
  | 'gif_uri'
  | 'gif_available'
>;

type VideoPreviewSource = Partial<Record<keyof VideoPreviewFields, unknown>>;

const normalizeDurablePreviewUri = (value: unknown): string | null => (
  typeof value === 'string' ? value.trim() || null : null
);

/**
 * Retain durable preview identities in application state and discard direct
 * renderer grants. Tiles acquire scoped, refreshable grants when they render.
 */
export const projectVideoPreviewFields = (
  rawVideo: VideoPreviewSource,
): VideoPreviewFields => {
  const thumbnailGcsUri = normalizeDurablePreviewUri(rawVideo.thumbnail_gcs_uri);
  const gifGcsUri = normalizeDurablePreviewUri(rawVideo.gif_gcs_uri);
  return {
    thumbnail_gcs_uri: thumbnailGcsUri,
    thumbnail_uri: null,
    thumbnail_available: Boolean(thumbnailGcsUri),
    gif_gcs_uri: gifGcsUri,
    gif_uri: null,
    gif_available: Boolean(gifGcsUri),
  };
};

/**
 * Preserve the existing object when its durable preview projection is already
 * canonical. This keeps gallery/status updates from remounting consumers that
 * key their media lifecycle to the selected video object.
 */
export const ensureDurableVideoPreviewFields = (video: Video): Video => {
  const projected = projectVideoPreviewFields(video);
  const alreadyCanonical = (
    video.thumbnail_gcs_uri === projected.thumbnail_gcs_uri
    && (video.thumbnail_uri ?? null) === projected.thumbnail_uri
    && video.thumbnail_available === projected.thumbnail_available
    && video.gif_gcs_uri === projected.gif_gcs_uri
    && (video.gif_uri ?? null) === projected.gif_uri
    && video.gif_available === projected.gif_available
  );
  return alreadyCanonical ? video : { ...video, ...projected };
};

/**
 * A montage event publishes durable provenance, not a client grant. Clear any
 * superseded direct URL so the tile requests the new preview generation.
 */
export const applyVideoAssetsReadyEvent = (
  video: Video,
  event: ProcessingStreamEvent,
): Video => {
  if (event.event_type !== 'video.assets_ready' || event.video_id !== video.video_id) {
    return video;
  }
  const eventVideo = event.payload?.video;
  if (!eventVideo) {
    return video;
  }
  const gifGcsUri = eventVideo.gif_gcs_uri === undefined ? (video.gif_gcs_uri || null) : normalizeDurablePreviewUri(eventVideo.gif_gcs_uri);
  const thumbnailGcsUri = eventVideo.thumbnail_gcs_uri === undefined ? video.thumbnail_gcs_uri : normalizeDurablePreviewUri(eventVideo.thumbnail_gcs_uri);
  if (
    thumbnailGcsUri === video.thumbnail_gcs_uri
    && gifGcsUri === (video.gif_gcs_uri || null)
    && !video.gif_uri
    && video.gif_available === Boolean(gifGcsUri)
  ) return video;
  return {
    ...video,
    ...(eventVideo.thumbnail_gcs_uri !== undefined ? { thumbnail_gcs_uri: thumbnailGcsUri, thumbnail_uri: null, thumbnail_available: Boolean(thumbnailGcsUri) } : {}),
    gif_gcs_uri: gifGcsUri,
    gif_uri: null,
    gif_available: Boolean(gifGcsUri),
  };
};
